import { useCallback } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"
import { useUpdateEffect } from "react-use"

import {
    useCoachingChatSocketEmitter as useSockerEmitter,
    useCoachingChatSocketReceiver as useSocketReceiver
} from "../api"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatSocketEmitterEventName as ESockerEmitterEventName,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatSocketReadyEventResponse as TConversationReadyEventResponse,
    type TCoachingChatSocketJoinConversationEventPayload as TJoinConversationEventPayload,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"

function useCoachingChatConversationJoiner({ entityId, entityMode, wasChatCompleted }: _TParams): void {
    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const apiMode: EConversationApiMode = useConversationApiMode()

    const isSocketMode: boolean = eq(apiMode, EConversationApiMode.Sockets)

    const isFullMode: boolean = eq(entityMode, EEntityMode.Full)

    const shouldRunBasedOnStatus: boolean = includes<EConversationStatus>(
        [EConversationStatus.Created, EConversationStatus.Connected, EConversationStatus.InProgress], // inProgress for api switch on started conversation
        conversationStatus
    )

    const { emit }: ReturnType<typeof useSockerEmitter<TJoinConversationEventPayload>> =
        useSockerEmitter<TJoinConversationEventPayload>(ESockerEmitterEventName.JoinConversation)

    const handler: (resp: TConversationReadyEventResponse) => void = useCallback(
        (resp: TConversationReadyEventResponse): void => {
            setConversationApiMode(EConversationApiMode.Sockets)

            if (isNumber(resp.conversation_id)) conversationDispatcher.changeStatus(EConversationStatus.InProgress)
        },
        [conversationDispatcher]
    )

    useSocketReceiver<TConversationReadyEventResponse>({
        isEnabled: isSocketMode && shouldRunBasedOnStatus && !wasChatCompleted && isFullMode,
        event: ESocketListenerEventName.Ready,
        handler
    })

    useUpdateEffect((): void => {
        if (!isSocketMode || !isFullMode || wasChatCompleted) return void 0

        if (shouldRunBasedOnStatus && isNumber(conversationId)) void emit({ conversation_id: conversationId })

        return void 0
    }, [shouldRunBasedOnStatus, isSocketMode, conversationId, isFullMode, wasChatCompleted])
}

export { useCoachingChatConversationJoiner }
